import React, { useState, useEffect } from 'react';
import type { User, UserRole, Organization, AppSection, CandidateViewType } from './types';
import { AuthService, DataService } from './services/firebase';
import { Layout } from './components/Layout';
import { LoginPage, OnboardingPage } from './pages/AuthStyled';
import { AdminDashboard } from './pages/Admin';
import { Button, Input, Modal } from './components/Common';
import { Shield, UserCircle, LogOut } from 'lucide-react';
import { TeamDataProvider, useTeamData } from './contexts/TeamDataContext';
import { AppSectionSelector } from './components/AppSectionSelector';
import { CandidateApp } from './pages/CandidateApp';
import { MinipageRoute, isMinipagePath } from './pages/minipage/MinipageRoute';

export type AdminViewType = 'dashboard' | 'orgs' | 'admins' | 'candidates' | 'pathways' | 'ojt' | 'notifications' | 'messaging';

function AppContent() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeOrgName, setActiveOrgName] = useState<string>('');
  const [activeSubTeamNames, setActiveSubTeamNames] = useState<string>('');
  
  // Admin Navigation State - Default to dashboard
  const [adminView, setAdminView] = useState<AdminViewType>('dashboard');
  const [appSection, setAppSection] = useState<AppSection | null>(null);
  const [candidateView, setCandidateView] = useState<CandidateViewType>('my-pathways');
  const [selectedPathwayId, setSelectedPathwayId] = useState<string | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  // Get team data context
  const teamData = useTeamData();

  // Global Profile Modal State
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Initial Load and Auth Subscription
  useEffect(() => {
    const unsubscribe = AuthService.subscribeToAuth((currentUser) => {
      setUser(currentUser);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Update Org Name and Sub-team names when user context changes
  useEffect(() => {
    const fetchOrgDetails = () => {
        console.log('🔍 Fetching org details:', { 
            hasUser: !!user, 
            activeOrgId: user?.activeOrgId, 
            hasTeamsadminnest: !!user?.teamsadminnest,
            orgIds: user?.orgIds 
        });
        
        if (user && user.activeOrgId && user.teamsadminnest) {
            try {
              // Helper to find org in teamsadminnest tree
              const findOrgInNest = (teamid: string, nest: any): { name: string, children?: any } | null => {
                if (!nest || typeof nest !== 'object') return null;
                
                if (nest[teamid]) return nest[teamid];
                
                for (const key of Object.keys(nest)) {
                  if (nest[key].children) {
                    const found = findOrgInNest(teamid, nest[key].children);
                    if (found) return found;
                  }
                }
                return null;
              };

              const active = findOrgInNest(user.activeOrgId, user.teamsadminnest);
              console.log('🏢 Found active org:', active);
              setActiveOrgName(active?.name || '');

              // Recursive function to get all descendant names from teamsadminnest
              const getDescendantNames = (children: any): string[] => {
                  if (!children || typeof children !== 'object') return [];
                  let names: string[] = [];
                  Object.keys(children).forEach(key => {
                      if (children[key].name) {
                          names.push(children[key].name);
                          if (children[key].children) {
                              names = [...names, ...getDescendantNames(children[key].children)];
                          }
                      }
                  });
                  return names;
              };

              if (active) {
                  // Check if the user wants to include sub-teams in the context
                  if (user.activeOrgIncludesSubTeams && active.children) {
                    const subNames = getDescendantNames(active.children);
                    setActiveSubTeamNames(subNames.join(', '));
                  } else {
                    setActiveSubTeamNames('');
                  }
              } else {
                  setActiveSubTeamNames('');
              }

            } catch (e) {
              console.error("Failed to fetch org details from teamsadminnest", e);
            }
        } else {
            setActiveOrgName('');
            setActiveSubTeamNames('');
        }
    };
    fetchOrgDetails();
  }, [user]);

  // Load team data when activeOrgId or includeSubTeams changes
  useEffect(() => {
    const loadData = async () => {
      if (!user || !user.activeOrgId) {
        console.log('⏭️ Skipping team data load - no active org');
        teamData.clearData();
        return;
      }

      console.log('🔄 Loading team data for:', user.activeOrgId);
      teamData.setIsLoading(true);
      
      try {
        // Load team document
        const team = await DataService.loadTeamDocument(user.activeOrgId);
        if (!team) {
          teamData.clearData();
          return;
        }

        // Load admins (pass team data to check parent for removability)
        const admins = await DataService.loadAdmins(team.adminslist || [], team);
        teamData.setAdmins(admins);
        
        // Load trainees (candidates)
        const { candidates } = await DataService.loadTrainees(
          team.trainees || {},
          user.activeOrgIncludesSubTeams || false
        );
        teamData.setCandidates(candidates);
        
        // Load available users for "add existing"
        // This loads all users from all accessible teams, excluding those already on current team as trainees
        const availableUsers = await DataService.loadAvailableUsers(
          user.teamsadminnest || {},
          user.activeOrgId,
          'trainees'
        );
        teamData.setAvailableUsers(availableUsers);

        console.log('✅ Team data loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load team data:', error);
        teamData.clearData();
      } finally {
        teamData.setIsLoading(false);
      }
    };

    loadData();
  }, [user?.activeOrgId, user?.activeOrgIncludesSubTeams]);

  // Sync profile form when user or modal changes
  useEffect(() => {
    if (user && isProfileOpen) {
        setProfileEmail(user.email);
        setProfilePhone(user.phoneNumber || '');
    }
  }, [user, isProfileOpen]);

  const handleLogin = () => {
    // Auth listener handles state update
  };

  const handleLogout = async () => {
    setIsProfileOpen(false); // Close modal before logging out
    setAppSection(null);
    await AuthService.logout();
    // User state will be cleared by listener
  };

  const handleUpdateUser = async () => {
      if (user) {
        try {
           const updatedUser = await AuthService.getCurrentUser();
           if (updatedUser) {
               setUser(updatedUser);
           }
        } catch (e) {
            console.error("Failed to refresh user profile", e);
        }
      }
  };

  const handleSwitchOrg = async (orgId: string, includeSubTeams: boolean) => {
      console.log('🔄 handleSwitchOrg called:', { orgId, includeSubTeams, userId: user?.id });
      if (!user) {
          console.warn('❌ No user found, cannot switch org');
          return;
      }
      try {
          // Save to userdetails with SR_ prefix
          await AuthService.updateUserProfile(user.id, { 
              SR_activeteam: orgId,
              SR_includesubteams: includeSubTeams 
          });
          console.log('✅ Org switched successfully, refreshing user...');
          handleUpdateUser();
      } catch (error) {
          console.error('❌ Failed to switch org:', error);
      }
  };

  const handleToggleSubTeams = async (enabled: boolean) => {
      console.log('🔀 handleToggleSubTeams called:', { enabled, userId: user?.id, activeOrgId: user?.activeOrgId });
      if (!user || !user.activeOrgId) {
          console.warn('❌ No user or activeOrgId, cannot toggle sub teams');
          return;
      }
      try {
          // Save to userdetails with SR_ prefix
          await AuthService.updateUserProfile(user.id, { 
              SR_includesubteams: enabled 
          });
          console.log('✅ Sub teams toggled successfully, refreshing user...');
          handleUpdateUser();
      } catch (error) {
          console.error('❌ Failed to toggle sub teams:', error);
      }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setIsSavingProfile(true);
    try {
        await AuthService.updateUserProfile(user.id, { email: profileEmail, phoneNumber: profilePhone });
        await handleUpdateUser();
        setIsProfileOpen(false);
    } catch (e) {
        console.error(e);
        alert('Failed to update profile');
    } finally {
        setIsSavingProfile(false);
    }
  };

  if (isLoading) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-aero-50 text-aero-600">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aero-600"></div>
        </div>
    );
  }

  // Not Logged In
  if (!user) {
    return (
        <LoginPage onLogin={handleLogin} />
    );
  }

  // Determine if user is an admin
  const isAdmin = user.teamsadminnest && Object.keys(user.teamsadminnest).length > 0;

  // Non-admin users go directly to candidate app
  if (!isAdmin) {
    return (
      <CandidateApp
        user={user}
        currentView={candidateView}
        onViewChange={setCandidateView}
        selectedPathwayId={selectedPathwayId}
        selectedBlockId={selectedBlockId}
        onSelectPathway={(id) => { setSelectedPathwayId(id); setCandidateView('pathway-detail'); }}
        onSelectBlock={(id) => { setSelectedBlockId(id); setCandidateView('block-detail'); }}
        onStartOjtSession={(blockId) => { setSelectedBlockId(blockId); setCandidateView('ojt-session'); }}
        onLogout={handleLogout}
        onSwitchToAdmin={() => setAppSection('admin')}
        isAdmin={false}
      />
    );
  }

  // Admin users who haven't chosen a section yet see the selector
  if (!appSection) {
    return (
      <AppSectionSelector
        userName={user.name}
        onSelectAdmin={() => setAppSection('admin')}
        onSelectCandidate={() => setAppSection('candidate')}
        onLogout={handleLogout}
      />
    );
  }

  // Admin chose candidate app
  if (appSection === 'candidate') {
    return (
      <CandidateApp
        user={user}
        currentView={candidateView}
        onViewChange={setCandidateView}
        selectedPathwayId={selectedPathwayId}
        selectedBlockId={selectedBlockId}
        onSelectPathway={(id) => { setSelectedPathwayId(id); setCandidateView('pathway-detail'); }}
        onSelectBlock={(id) => { setSelectedBlockId(id); setCandidateView('block-detail'); }}
        onStartOjtSession={(blockId) => { setSelectedBlockId(blockId); setCandidateView('ojt-session'); }}
        onLogout={handleLogout}
        onSwitchToAdmin={() => setAppSection('admin')}
        isAdmin={true}
      />
    );
  }

  // Admin Onboarding: Force create an organization if admin has no orgs yet
  // (User has teamsadminnest so they're admin, but no orgIds means they need to create/join an org)
  if (user.teamsadminnest && Object.keys(user.teamsadminnest).length > 0 && (!user.orgIds || user.orgIds.length === 0)) {
     return (
        <OnboardingPage onComplete={handleUpdateUser} />
     );
  }

  return (
    <>
        <Layout 
            user={user} 
            activeOrgName={activeOrgName}
            activeSubTeamNames={activeSubTeamNames}
            onProfileClick={() => setIsProfileOpen(true)}
            currentAdminView={adminView}
            onAdminViewChange={setAdminView}
            onToggleSubTeams={handleToggleSubTeams}
        >
            <AdminDashboard
                user={user}
                activeOrgId={user.activeOrgId}
                onUpdateUser={handleUpdateUser}
                onSwitchOrg={handleSwitchOrg}
                currentView={adminView}
                onSwitchView={setAdminView}
            />
        </Layout>

        {/* Global Profile Modal */}
        <Modal isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} title="User Profile">
            <div className="space-y-6">
                <div className="flex items-center gap-4 p-4 bg-aero-50/50 rounded-xl border border-aero-100">
                    <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-aero-600 border border-aero-200 shadow-sm">
                        <Shield className="w-8 h-8" />
                    </div>
                    <div>
                        <h4 className="text-lg font-bold text-aero-900">{user.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-aero-600 text-white uppercase tracking-wider">
                                ADMIN
                            </span>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <Input label="Email Address" value={profileEmail} onChange={e => setProfileEmail(e.target.value)} />
                    <Input label="Phone Number" value={profilePhone} onChange={e => setProfilePhone(e.target.value)} placeholder="+1 (555) 000-0000" />
                    <p className="text-[10px] text-aero-400 italic">Updating these details will sync across all organization training records.</p>
                </div>

                <div className="flex flex-col gap-3 pt-6 border-t border-aero-100">
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setIsProfileOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveProfile} isLoading={isSavingProfile}>Save Changes</Button>
                    </div>
                    
                    <button 
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 p-3 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors mt-2 text-sm font-medium border border-red-100"
                    >
                        <LogOut className="w-4 h-4" />
                        Log Out
                    </button>
                </div>
            </div>
        </Modal>
    </>
  );
}

// Wrap AppContent with TeamDataProvider
function App() {
  // Mini-page route dispatch — runs before any auth / team-data context is built
  // so a candidate or lead-hand opening a /m/{token} link bypasses the entire
  // app shell. See docs/OJT_AGENT.md §5.
  if (typeof window !== 'undefined' && isMinipagePath(window.location.pathname)) {
    return <MinipageRoute />;
  }

  return (
    <TeamDataProvider>
      <AppContent />
    </TeamDataProvider>
  );
}

export default App;